import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import SupportPanel from '@/components/support/SupportPanel'
import Card from '@/components/ui/Card'
import { useAuthContext } from '@/lib/auth-context'
import {
  emitLockerRoomConfigLedger,
} from '@/lib/coreApi'
import { vendorCopy } from '@/pages/teacher/_shop-helpers'
import { supabase } from '@/supabase/client'
import CoachShareLinks from './CoachShareLinks'
import CoachShoppingPanel from './CoachShoppingPanel'
import ProspectLockerView from './ProspectLockerView'
import RosterManager from '@/components/locker/RosterManager'
import { useLockerProspect } from '@/hooks/useLockerProspect'

type LockerRoom = {
  id: string
  coach_user_id?: string | null
  name?: string | null
  public_name?: string | null
  public_config?: Record<string, any> | null
}

type LockerRoomMember = {
  id?: string | number | null
  locker_room_id?: string | null
  prospect_id: number
  role?: string | null
  active?: boolean | null
  prospect?: {
    id: number
    name?: string | null
    avatar_url?: string | null
    headline?: string | null
    status?: string | null
  } | null
}

type LockerRoomProspect = NonNullable<LockerRoomMember['prospect']>

type ShoppingSession = {
  id?: string | number
  partner?: string | null
  status?: string | null
  budget_cents?: number | null
  metadata?: Record<string, unknown> | null
  started_at?: string | null
  updated_at?: string | null
}

type ProspectNotes = {
  annual_budget_cents?: number | null
  spent_cents?: number | null
  wallet_credits?: number | null
  shopping_sessions?: ShoppingSession[]
}

type ProspectBudgetPlan = {
  prospect_id: number
  remaining_cents?: number | null
  annual_limit_cents?: number | null
  monthly_limit_cents?: number | null
  soft_monthly_limit_cents?: number | null
  planned_annual_allocation_cents?: number | null
}

type ProspectActivity = {
  lastSession: ShoppingSession | null
  monthlyCount: number
  totalCount: number
}

function formatProspectName(prospect?: LockerRoomMember['prospect'], id?: number) {
  if (!prospect && id) return `Prospect #${id}`
  if (!prospect) return 'Prospect'
  return prospect.name || prospect.headline || `Prospect #${prospect.id}`
}

function formatCurrency(cents?: number | null) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((cents as number) / 100)
}

function coerceNumber(value: unknown) {
  return Number.isFinite(value) ? (value as number) : null
}

function formatSessionVendor(metadata: Record<string, unknown>) {
  const vendorKey = (metadata.vendor || metadata.partner || '') as string
  const vendorInfo = vendorKey in vendorCopy ? vendorCopy[vendorKey as keyof typeof vendorCopy] : null
  return vendorInfo?.label || vendorKey || 'Unknown merchant'
}

function formatSessionAmount(metadata: Record<string, unknown>) {
  const amount = coerceNumber((metadata as Record<string, unknown>).amount_cents)
  if (!Number.isFinite(amount)) return '—'
  return formatCurrency(amount)
}

function parseDate(value?: string | null) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export default function LockerRoomHome() {
  const { profile, user } = useAuthContext() as any
  const { prospect, loading, error, budget } = useLockerProspect()
  const navigate = useNavigate()

  const [lockerRoom, setLockerRoom] = useState<LockerRoom | null>(null)
  const [lockerRoomLoading, setLockerRoomLoading] = useState(false)
  const [lockerRoomError, setLockerRoomError] = useState('')

  const [roster, setRoster] = useState<LockerRoomMember[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterError, setRosterError] = useState('')
  const [rosterMessage, setRosterMessage] = useState('')
  const [availableProspects, setAvailableProspects] = useState<LockerRoomProspect[]>([])
  const [linkDraft, setLinkDraft] = useState('')

  const [budgetPlans, setBudgetPlans] = useState<Record<number, ProspectBudgetPlan>>({})
  const [budgetPlansLoading, setBudgetPlansLoading] = useState(false)
  const [budgetPlansError, setBudgetPlansError] = useState('')

  const [activityByProspect, setActivityByProspect] = useState<Record<number, ProspectActivity>>({})
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState('')

  const [publicNameInput, setPublicNameInput] = useState('')
  const [heroHeadlineInput, setHeroHeadlineInput] = useState('')
  const [heroSubheadlineInput, setHeroSubheadlineInput] = useState('')
  const [publicConfigSaving, setPublicConfigSaving] = useState(false)
  const [publicConfigMessage, setPublicConfigMessage] = useState('')
  const [publicConfigError, setPublicConfigError] = useState('')

  // Roster groups
  const [rosterGroups, setRosterGroups] = useState<Array<{ id: string; name: string; description: string | null; color: string | null; member_count: number }>>([])
  const [groupMemberships, setGroupMemberships] = useState<Record<number, string[]>>({})

  // Auth token — must be declared before any useCallback that closes over it
  // (loadRoster, loadGroups, etc.) to avoid a temporal dead zone error.
  const [authToken, setAuthToken] = useState('')
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setAuthToken(data?.session?.access_token || '') })
  }, [])

  const isCoach = profile?.role === 'coach'
  const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

  const supportContextMeta = useMemo(
    () => ({
      surface: 'locker_room' as const,
      locker_room_id: lockerRoom?.id || undefined,
      prospect_id: prospect?.id || undefined,
    }),
    [lockerRoom?.id, prospect?.id],
  )

  const supportMetadata = useMemo(
    () => ({ ...supportContextMeta, context: 'locker_room', role: profile?.role || 'prospect' }),
    [supportContextMeta, profile?.role],
  )

  const loadLockerRoom = useCallback(async () => {
    if (!isCoach || !user?.id) return
    setLockerRoomLoading(true)
    setLockerRoomError('')
    const { data, error: fetchError } = await supabase
      .from('locker_rooms')
      .select('id, coach_user_id, name, public_name, public_config')
      .eq('coach_user_id', user.id)
      .is('archived_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (fetchError) {
      setLockerRoom(null)
      setLockerRoomError(fetchError.message || 'Unable to load locker room')
    } else {
      const room = data as LockerRoom | null
      setLockerRoom(room)
      setPublicNameInput(room?.public_name || room?.name || '')
      setHeroHeadlineInput(room?.public_config?.hero?.headline || '')
      setHeroSubheadlineInput(room?.public_config?.hero?.subheadline || '')
    }

    setLockerRoomLoading(false)
  }, [isCoach, user?.id])

  const ensureLockerRoom = useCallback(async () => {
    if (lockerRoom?.id) return lockerRoom
    if (!isCoach || !user?.id) return null

    setLockerRoomLoading(true)
    setLockerRoomError('')

    const fallbackName = 'Locker Room'
    const { data, error } = await supabase
      .from('locker_rooms')
      .insert({ coach_user_id: user.id, name: fallbackName, public_name: publicNameInput?.trim() || fallbackName })
      .select('id, coach_user_id, name, public_name, public_config')
      .maybeSingle()

    if (error) {
      setLockerRoomError(error.message || 'Unable to create locker room')
      setLockerRoomLoading(false)
      return null
    }

    const nextRoom = data as LockerRoom | null
    if (!nextRoom) {
      setLockerRoomLoading(false)
      return null
    }
    setLockerRoom(nextRoom)
    setPublicNameInput(nextRoom.public_name || nextRoom.name || '')
    setHeroHeadlineInput(nextRoom.public_config?.hero?.headline || '')
    setHeroSubheadlineInput(nextRoom.public_config?.hero?.subheadline || '')
    setLockerRoomLoading(false)
    return nextRoom
  }, [isCoach, lockerRoom?.id, publicNameInput, user?.id])

  const loadCoachProspects = useCallback(async () => {
    const coachId = profile?.coach_id || user?.id
    if (!isCoach || !coachId) return
    const { data, error: fetchError } = await supabase
      .from('prospects')
      .select('id, name, avatar_url, headline, status')
      .eq('coach_user_id', coachId)
      .order('created_at', { ascending: false })

    if (fetchError) {
      setRosterError(fetchError.message || 'Unable to load prospects')
      return
    }
    const list = (data || []) as LockerRoomProspect[]
    setAvailableProspects(list)
  }, [isCoach, profile?.coach_id, user?.id])

  const loadRoster = useCallback(async () => {
    if (!isCoach || !lockerRoom?.id || !authToken) return
    setRosterLoading(true)
    setRosterError('')
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/locker/${lockerRoom.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message || 'Unable to load roster')
      }
      const json = await res.json()
      setRoster((json.data?.roster || []) as unknown as LockerRoomMember[])
    } catch (err: any) {
      setRosterError(err?.message || 'Unable to load roster')
    } finally {
      setRosterLoading(false)
    }
  }, [isCoach, lockerRoom?.id, authToken])

  const loadBudgetPlans = useCallback(
    async (prospectIds: number[]) => {
      if (!prospectIds.length) {
        setBudgetPlans({})
        return
      }

      setBudgetPlansLoading(true)
      setBudgetPlansError('')

      const { data, error: fetchError } = await supabase
        .from('prospect_budgets')
        .select(
          'prospect_id, remaining_cents, annual_limit_cents, monthly_limit_cents, soft_monthly_limit_cents, planned_annual_allocation_cents',
        )
        .in('prospect_id', prospectIds)

      if (fetchError) {
        setBudgetPlansError(fetchError.message || 'Unable to load budget plans')
        setBudgetPlans({})
        setBudgetPlansLoading(false)
        return
      }

      const nextPlans = (data || []).reduce((acc, row) => {
        const plan = row as ProspectBudgetPlan
        acc[plan.prospect_id] = plan
        return acc
      }, {} as Record<number, ProspectBudgetPlan>)

      setBudgetPlans(nextPlans)
      setBudgetPlansLoading(false)
    },
    [setBudgetPlans],
  )

  const loadShoppingActivity = useCallback(
    async (prospectIds: number[]) => {
      if (!prospectIds.length) {
        setActivityByProspect({})
        return
      }

      setActivityLoading(true)
      setActivityError('')

      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      startOfMonth.setHours(0, 0, 0, 0)

      try {
        const results = await Promise.all(
          prospectIds.map(async (prospectId) => {
            const [{ data: lastSession }, monthlyCountResult, totalCountResult] = await Promise.all([
              supabase
                .from('shopping_sessions')
                .select('id, status, metadata, started_at, updated_at')
                .contains('metadata', { prospect_id: prospectId })
                .order('started_at', { ascending: false })
                .limit(1)
                .maybeSingle(),
              supabase
                .from('shopping_sessions')
                .select('id', { count: 'exact', head: true })
                .contains('metadata', { prospect_id: prospectId })
                .gte('started_at', startOfMonth.toISOString()),
              supabase
                .from('shopping_sessions')
                .select('id', { count: 'exact', head: true })
                .contains('metadata', { prospect_id: prospectId }),
            ])

            const monthlyCount = Number.isFinite(monthlyCountResult.count) ? (monthlyCountResult.count as number) : 0
            const totalCount = Number.isFinite(totalCountResult.count) ? (totalCountResult.count as number) : 0

            return {
              prospectId,
              activity: {
                lastSession: (lastSession as ShoppingSession | null) || null,
                monthlyCount,
                totalCount,
              },
            }
          }),
        )

        const nextActivity = results.reduce((acc, item) => {
          acc[item.prospectId] = item.activity
          return acc
        }, {} as Record<number, ProspectActivity>)

        setActivityByProspect(nextActivity)
        setActivityLoading(false)
      } catch (err: any) {
        console.error('[locker room] activity load failed', err)
        setActivityError(err?.message || 'Unable to load shopping activity')
        setActivityByProspect({})
        setActivityLoading(false)
      }
    },
    [setActivityByProspect],
  )

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!active || !isCoach) return
      await loadLockerRoom()
      await loadCoachProspects()
    }
    load()
    return () => {
      active = false
    }
  }, [isCoach, loadCoachProspects, loadLockerRoom])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!active) return
      await loadRoster()
    }
    load()
    return () => {
      active = false
    }
  }, [loadRoster])

  useEffect(() => {
    const ids = roster.map((member) => member.prospect_id)
    loadBudgetPlans(ids)
    loadShoppingActivity(ids)
  }, [loadBudgetPlans, loadShoppingActivity, roster])

  const linkableProspects = useMemo(() => {
    const linked = new Set(roster.map((member) => member.prospect_id))
    return availableProspects.filter((prospect) => !linked.has(prospect.id))
  }, [availableProspects, roster])

  useEffect(() => {
    if (!linkDraft && linkableProspects.length) {
      setLinkDraft(String(linkableProspects[0].id))
    }
  }, [linkDraft, linkableProspects])

  // ─── Roster groups loading ──────────────────────────────────────────────

  const loadGroups = useCallback(async () => {
    if (!isCoach) return
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/groups`, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      })
      if (!res.ok) return
      const json = await res.json()
      setRosterGroups(json.data?.groups || [])
    } catch { /* ignore */ }
  }, [isCoach, authToken, CORE_API])

  const loadGroupMemberships = useCallback(async () => {
    if (!rosterGroups.length) { setGroupMemberships({}); return }
    const map: Record<number, string[]> = {}
    for (const g of rosterGroups) {
      try {
        const res = await fetch(`${CORE_API}/api/v1/roster/groups/${g.id}/members`, {
          headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        })
        if (!res.ok) continue
        const json = await res.json()
        for (const m of json.data?.members || []) {
          if (!map[m.prospect_id]) map[m.prospect_id] = []
          map[m.prospect_id].push(g.id)
        }
      } catch { /* ignore */ }
    }
    setGroupMemberships(map)
  }, [rosterGroups, authToken, CORE_API])

  useEffect(() => { loadGroups() }, [loadGroups])
  useEffect(() => { loadGroupMemberships() }, [loadGroupMemberships])

  const handleStatusChange = useCallback(async (prospectId: number, status: string) => {
    try {
      await fetch(`${CORE_API}/api/v1/roster/member/${prospectId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
      await loadRoster()
    } catch (err) { console.error('status change failed', err) }
  }, [authToken, CORE_API, loadRoster])

  const handleBulkStatus = useCallback(async (prospectIds: number[], status: string) => {
    try {
      await fetch(`${CORE_API}/api/v1/roster/bulk-status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_ids: prospectIds, status }),
      })
      await loadRoster()
    } catch (err) { console.error('bulk status failed', err) }
  }, [authToken, CORE_API, loadRoster])

  const handleGroupCreate = useCallback(async (name: string, color: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color }),
    })
    await loadGroups()
  }, [authToken, CORE_API, loadGroups])

  const handleGroupUpdate = useCallback(async (groupId: string, name: string, color: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, color }),
    })
    await loadGroups()
  }, [authToken, CORE_API, loadGroups])

  const handleGroupDelete = useCallback(async (groupId: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
    })
    await loadGroups()
    await loadGroupMemberships()
  }, [authToken, CORE_API, loadGroups, loadGroupMemberships])

  const handleGroupToggle = useCallback(async (groupId: string, prospectId: number, add: boolean) => {
    if (add) {
      await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospect_ids: [prospectId] }),
      })
    } else {
      await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members/${prospectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      })
    }
    await loadGroupMemberships()
  }, [authToken, CORE_API, loadGroupMemberships])

  const handleBulkGroupAssign = useCallback(async (groupId: string, prospectIds: number[]) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ prospect_ids: prospectIds }),
    })
    await loadGroupMemberships()
  }, [authToken, CORE_API, loadGroupMemberships])

  const handleRosterRefresh = useCallback(async () => {
    await loadRoster()
    await loadGroups()
    await loadGroupMemberships()
  }, [loadRoster, loadGroups, loadGroupMemberships])

  const toggleMemberActive = useCallback(
    async (member: LockerRoomMember, nextActive: boolean) => {
      if (!lockerRoom?.id) return
      setRosterError('')
      setRosterMessage('')
      const { error } = await (supabase as any)
        .from('locker_room_members')
        .update({ active: nextActive })
        .eq('locker_room_id', lockerRoom.id)
        .eq('prospect_id', member.prospect_id)
        .eq('role', member.role || 'athlete')

      if (error) {
        setRosterError(error.message || 'Unable to update roster member')
        return
      }

      setRosterMessage(nextActive ? 'Prospect linked to locker room.' : 'Prospect unlinked from locker room.')
      await loadRoster()
    },
    [loadRoster, lockerRoom?.id],
  )

  const removeMember = useCallback(
    async (member: LockerRoomMember) => {
      if (!lockerRoom?.id) return
      setRosterError('')
      const { error } = await (supabase as any)
        .from('locker_room_members')
        .delete()
        .eq('locker_room_id', lockerRoom.id)
        .eq('prospect_id', member.prospect_id)
        .eq('role', member.role || 'athlete')

      if (error) {
        setRosterError(error.message || 'Unable to remove member')
        return
      }
      setRosterMessage('Athlete removed from roster.')
      await loadRoster()
    },
    [loadRoster, lockerRoom?.id],
  )

  const linkProspectById = useCallback(
    async (prospectId: number) => {
      const locker = await ensureLockerRoom()
      if (!locker?.id) {
        setRosterError('Create a locker room before linking prospects.')
        return
      }
      setRosterLoading(true)
      setRosterError('')
      setRosterMessage('')
      const { error } = await (supabase as any)
        .from('locker_room_members')
        .upsert(
          { locker_room_id: locker.id, prospect_id: prospectId, role: 'athlete', active: true },
          { onConflict: 'locker_room_id,prospect_id,role' },
        )
      if (error) {
        setRosterError(error.message || 'Unable to link prospect')
        setRosterLoading(false)
        return
      }
      setRosterMessage('Prospect linked to locker room.')
      await loadRoster()
      setRosterLoading(false)
    },
    [ensureLockerRoom, loadRoster],
  )

  const linkProspectToLocker = useCallback(async () => {
    if (!linkDraft) {
      setRosterError('Select a prospect to link')
      return
    }

    const locker = await ensureLockerRoom()
    if (!locker?.id) {
      setRosterError('Create a locker room before linking prospects.')
      return
    }

    const prospectId = Number(linkDraft)
    if (!Number.isFinite(prospectId)) {
      setRosterError('Select a valid prospect to link')
      return
    }
    setRosterLoading(true)
    setRosterError('')
    setRosterMessage('')
    const { error } = await (supabase as any)
      .from('locker_room_members')
      .upsert(
        { locker_room_id: locker.id, prospect_id: prospectId, role: 'athlete', active: true },
        { onConflict: 'locker_room_id,prospect_id,role' },
      )

    if (error) {
      setRosterError(error.message || 'Unable to link prospect')
      setRosterLoading(false)
      return
    }

    setRosterMessage('Prospect linked to locker room.')
    setLinkDraft('')
    await loadRoster()
    setRosterLoading(false)
  }, [ensureLockerRoom, linkDraft, loadRoster])

  const savePublicSettings = useCallback(async () => {
    if (!lockerRoom?.id || !isCoach) return
    setPublicConfigSaving(true)
    setPublicConfigError('')
    setPublicConfigMessage('')

    const nextConfig = {
      ...(lockerRoom.public_config || {}),
      hero: {
        ...(lockerRoom.public_config?.hero || {}),
        headline: heroHeadlineInput || null,
        subheadline: heroSubheadlineInput || null,
      },
    }

    const { data, error } = await (supabase as any)
      .from('locker_rooms')
      .update({
        public_name: publicNameInput?.trim() || lockerRoom.name || 'Locker Room',
        public_config: nextConfig,
      })
      .eq('id', lockerRoom.id)
      .select('id, coach_user_id, name, public_name, public_config')
      .maybeSingle()

    if (error) {
      setPublicConfigError(error.message || 'Unable to save public settings')
      setPublicConfigSaving(false)
      return
    }

    emitLockerRoomConfigLedger({ lockerRoom: data }).catch((err) =>
      console.error('locker-room ledger emit failed', err),
    )

    setLockerRoom((data ?? null) as LockerRoom | null)
    setPublicConfigMessage('Public locker room settings updated.')
    setPublicConfigSaving(false)
  }, [heroHeadlineInput, heroSubheadlineInput, isCoach, lockerRoom, publicNameInput])

  const activeRoster = useMemo(() => roster.filter((member) => member.active !== false), [roster])
  const inactiveCount = roster.length - activeRoster.length
  const activeProspectIds = useMemo(() => activeRoster.map((member) => member.prospect_id), [activeRoster])

  const lockerAnnualBudget = useMemo(() => {
    return activeProspectIds.reduce((total, prospectId) => {
      const remaining = coerceNumber(budgetPlans[prospectId]?.remaining_cents)
      return total + (remaining || 0)
    }, 0)
  }, [activeProspectIds, budgetPlans])

  const lockerMonthlyLimit = useMemo(() => {
    return activeProspectIds.reduce((total, prospectId) => {
      const plan = budgetPlans[prospectId]
      const softMonthly = coerceNumber(plan?.soft_monthly_limit_cents)
      const monthly = coerceNumber(plan?.monthly_limit_cents)
      return total + (softMonthly ?? monthly ?? 0)
    }, 0)
  }, [activeProspectIds, budgetPlans])

  const rosterDetails = useMemo(
    () =>
      roster.map((member) => {
        const plan = budgetPlans[member.prospect_id]
        const plannedAnnual =
          coerceNumber(plan?.planned_annual_allocation_cents) ??
          coerceNumber(plan?.annual_limit_cents) ??
          coerceNumber(plan?.remaining_cents) ??
          coerceNumber(((member.prospect as Record<string, unknown>)?.notes as ProspectNotes | undefined)?.annual_budget_cents)
        const activity = activityByProspect[member.prospect_id]
        const lastSession = activity?.lastSession || null
        const metadata = (lastSession?.metadata || {}) as Record<string, unknown>
        const lastDate = parseDate(lastSession?.started_at || lastSession?.updated_at)

        return {
          member,
          plannedAnnual,
          lastSession,
          lastVendor: lastSession ? formatSessionVendor(metadata) : '—',
          lastAmount: lastSession ? formatSessionAmount(metadata) : '—',
          lastDate,
          monthlyCount: activity?.monthlyCount || 0,
          totalCount: activity?.totalCount || 0,
        }
      }),
    [activityByProspect, budgetPlans, roster],
  )

  const purchaseSummary = useMemo(() => {
    const last = activeProspectIds.reduce(
      (acc, prospectId) => {
        const activity = activityByProspect[prospectId]
        const session = activity?.lastSession
        const date = parseDate(session?.started_at || session?.updated_at)
        if (session && date && (!acc.date || date > acc.date)) {
          return { session, date }
        }
        return acc
      },
      { session: null as ShoppingSession | null, date: null as Date | null },
    )

    const monthlyCount = activeProspectIds.reduce((total, prospectId) => total + (activityByProspect[prospectId]?.monthlyCount || 0), 0)
    const totalCount = activeProspectIds.reduce((total, prospectId) => total + (activityByProspect[prospectId]?.totalCount || 0), 0)

    return { lastSession: last.session, lastDate: last.date, monthlyCount, totalCount }
  }, [activeProspectIds, activityByProspect])

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-stone-900">Home</h2>
        <p className="text-sm text-stone-600">Budget, shopping, sharing, and full roster management.</p>
      </div>

      {/* Row 1: Key metric cards — compact 3-col */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Budget</p>
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase text-stone-500">
              {budgetPlansLoading ? 'Loading' : 'Ready'}
            </span>
          </div>
          {budgetPlansError ? <p className="text-xs text-brick">{budgetPlansError}</p> : null}
          <div className="flex items-baseline gap-4">
            <div>
              <p className="text-2xl font-bold text-stone-900">{formatCurrency(lockerAnnualBudget)}</p>
              <p className="text-[10px] uppercase tracking-wide text-stone-400">Annual</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-stone-700">{formatCurrency(lockerMonthlyLimit)}</p>
              <p className="text-[10px] uppercase tracking-wide text-stone-400">Monthly</p>
            </div>
          </div>
          <p className="text-xs text-stone-500">{activeRoster.length} active athlete{activeRoster.length !== 1 ? 's' : ''}</p>
          <Link to="/portal/prospect/budget" className="mt-1 inline-block text-xs font-semibold text-stone-700 underline hover:text-stone-900">
            Manage budgets &rarr;
          </Link>
        </Card>

        <Card className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Shopping</p>
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase text-stone-500">
              {activityLoading ? 'Loading' : 'Live'}
            </span>
          </div>
          {activityError ? <p className="text-xs text-brick">{activityError}</p> : null}
          <p className="text-2xl font-bold text-stone-900">{purchaseSummary.monthlyCount}</p>
          <p className="text-[10px] uppercase tracking-wide text-stone-400">Purchases this month</p>
          {purchaseSummary.lastSession ? (
            <p className="text-xs text-stone-500">
              Last: {formatSessionVendor((purchaseSummary.lastSession.metadata || {}) as Record<string, unknown>)}
              {' · '}
              {formatSessionAmount((purchaseSummary.lastSession.metadata || {}) as Record<string, unknown>)}
            </p>
          ) : (
            <p className="text-xs text-stone-400">No transactions yet</p>
          )}
          <Link to="/portal/prospect/shopping" className="mt-1 inline-block text-xs font-semibold text-stone-700 underline hover:text-stone-900">
            View activity &rarr;
          </Link>
        </Card>

        <Card className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Roster</p>
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase text-stone-500">
              {rosterLoading ? 'Loading' : `${roster.length}`}
            </span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{activeRoster.length}</p>
          <p className="text-[10px] uppercase tracking-wide text-stone-400">Active athletes</p>
          {inactiveCount > 0 && (
            <p className="text-xs text-stone-400">{inactiveCount} inactive</p>
          )}
          <a href="#roster" className="mt-1 inline-block text-xs font-semibold text-stone-700 underline hover:text-stone-900">
            Manage roster &darr;
          </a>
        </Card>
      </div>

      {/* Row 2: Action panels — 2-col */}
      <div className="grid gap-4 md:grid-cols-2">
        <CoachShareLinks />

        <Card className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Support</p>
              <h3 className="text-sm font-semibold text-stone-900">Locker room support</h3>
            </div>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Priority</span>
          </div>
          <SupportPanel
            roleLabel="Prospect"
            intro="Questions about onboarding or sponsorship? The Tower team can help here."
            defaultCategory="prospect_support"
            contextMeta={supportContextMeta}
            defaultMetadata={supportMetadata || undefined}
            prospectId={prospect?.id}
          />
        </Card>
      </div>

      {/* Roster summary — full management lives on /roster tab */}
      {isCoach ? (
        <Card id="roster" className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500">Roster</p>
              <h3 className="text-base font-semibold text-stone-900">{roster.length} total · {activeRoster.length} active</h3>
              <p className="text-sm text-stone-600">Manage sports, groups, statuses, and measurables on the Roster tab.</p>
            </div>
            <Link to="/portal/locker/roster" className="rounded-xl bg-stone-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800">
              Open Roster Manager →
            </Link>
          </div>
          {rosterError ? <p className="text-sm text-brick">{rosterError}</p> : null}
        </Card>
      ) : (
        <ProspectLockerView />
      )}

      {budget.error ? <p className="text-sm text-brick">{budget.error}</p> : null}
    </section>
  )
}
